import React, { useState } from "react"
import { Button } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTriangleExclamation,
  faXmark,
  faCopy,
} from "@fortawesome/free-solid-svg-icons"

import { ImportWarning, UnmappedColumnsPayload } from "types/dataImport.types"
import {
  groupImportWarnings,
  unmappedColumnCount,
  parseUnmappedPayload,
} from "features/DataImport/utils/parseImportWarnings"
import UnmappedColumnsModal from "features/DataImport/components/UnmappedColumnsModal.component"

interface ImportWarningsPanelProps {
  warnings: ImportWarning[]
  onDismiss: () => void
  onOpenEditor?: () => void
}

/**
 * Renders the outcome of a CSV + datamap import in two sections.
 *
 * Items a user can act on (renames, capitalisation mismatches, missing fields, conversion
 * failures) sit expanded in amber. The aggregated inventory of columns that were not
 * imported sits below, neutral and collapsed — scripting platforms always emit system
 * columns, so that bucket is expected noise and must not compete with real errors.
 *
 * @component
 */
const ImportWarningsPanel: React.FC<ImportWarningsPanelProps> = ({ warnings, onDismiss, onOpenEditor }) => {
  const [reviewing, setReviewing] = useState(false)

  if (warnings.length === 0) return null

  const { attention, informational } = groupImportWarnings(warnings)
  const notImportedCount = unmappedColumnCount(warnings)

  // Merge every [unmapped] message into one inventory. In practice the engine emits at most
  // one, but reducing avoids a silent truncation if that ever changes.
  const payload: UnmappedColumnsPayload | null = informational.reduce<UnmappedColumnsPayload | null>(
    (merged, warning) => {
      const parsed = parseUnmappedPayload(warning.text)
      if (!parsed) return merged
      if (!merged) return parsed

      return {
        count: merged.count + parsed.count,
        related: [...merged.related, ...parsed.related],
        other: [...merged.other, ...parsed.other],
      }
    },
    null,
  )

  const summary = [
    attention.length > 0 ? `${attention.length} need attention` : null,
    notImportedCount > 0 ? `${notImportedCount} columns not imported` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  const handleCopy = (): void => {
    void navigator.clipboard?.writeText(warnings.map((warning) => warning.text).join("\n"))
  }

  return (
    <div className="mx-4 mt-4">
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className={attention.length > 0 ? "text-amber-500" : "text-slate-400"}
            />
            <span className="text-sm font-medium text-slate-800">Import results</span>
            <span className="text-xs text-slate-500">{summary}</span>
          </div>
          <div className="flex items-center gap-1">
            {/* In the header, not below the list: the panel sits at the bottom of a
                scrolling column, so anything under the messages falls below the fold. */}
            {onOpenEditor && attention.length > 0 && (
              <Button className="mr-1" onClick={onOpenEditor}>
                Open datamap editor
              </Button>
            )}
            {payload && (
              <Button className="mr-1" onClick={() => setReviewing(true)}>
                Review {payload.count} not imported
              </Button>
            )}
            <button
              type="button"
              onClick={handleCopy}
              aria-label="Copy all import messages"
              title="Copy all import messages"
              className="dq-btn dq-btn--icon dq-btn--quiet"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss import results"
              className="dq-btn dq-btn--icon dq-btn--quiet"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>

        {attention.length > 0 && (
          <div className="max-h-48 overflow-y-auto p-3 bg-amber-50">
            <div className="space-y-1.5">
              {attention.map((warning, index) => (
                <div
                  key={`${warning.code}-${index}`}
                  className="text-sm text-amber-800 pl-3 border-l-2 border-amber-300 py-0.5"
                >
                  {warning.text}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* An engine build predating the JSON payload, or a body that would not parse:
            show whatever the message said rather than dropping it. When the payload does
            parse, the Review button in the header covers this. */}
        {informational.length > 0 && !payload && (
          <div className="border-t border-slate-200 px-4 py-2.5">
            <span className="text-sm text-slate-600 break-words">
              {informational.map((warning) => warning.text).join(" ")}
            </span>
          </div>
        )}
      </div>

      {payload && (
        <UnmappedColumnsModal
          open={reviewing}
          payload={payload}
          onClose={() => setReviewing(false)}
        />
      )}
    </div>
  )
}

export default ImportWarningsPanel
